import {
    AppNotificationType,
    BetaTestingStyle,
    IntegrationCapability,
    ReleaseStepType,
    AppStoreDefaultSettings,
    RegressionTestingStrategy,
    RetainedBuildsSettings,
} from '@/types';
import { StepDependencies } from '@/types/workflow';
import { Nullable } from '@/typescriptHelpers';
import { Destination } from './destinations';
import { AgeRatingDeclaration, AgeRatingQuestionSet } from '@/types/ageRating';

type NotificationSettingsByType = Record<AppNotificationType, Notification[]>;

export type StepConfig =
    | BetaTestingStepConfig
    | CIDistributionStepConfig
    | RegressionTestingStepConfig
    | MetadataStepConfig
    | RolloutStepConfig
    | ScreenshotsStepConfig
    | StoreReviewStepConfig
    | ReleaseStepConfig
    | SubmissionStepConfig
    | DefaultStepConfig; // catch all for all stepConfig types with no settings data

export interface StepConfigSummary {
    id: string;
    name: string;
    description: Nullable<string>;
    archetype: ReleaseStepType;
    destinations: Destination[];
}

export interface StepConfigBase extends StepConfigSummary {
    createdAt: string;
    updatedAt: string;
    deletedAt?: string;
    dependencies: StepDependencies;

    requiredCapabilities: IntegrationCapability[];
    optionalCapabilities: IntegrationCapability[];

    integrationInstallationIds: string[];

    notifications: NotificationSettingsByType;

    tagConfigId: Nullable<string>;
    branchConfigId: Nullable<string>;
    featureAffiliationConfigId: Nullable<string>;
}

export interface DefaultStepConfig extends StepConfigBase {
    // any other step type except the ones below
    archetype: ReleaseStepType;
    settings: null;
}

export interface BetaTestingStepConfig extends StepConfigBase {
    archetype: ReleaseStepType.BetaTesting;
    settings: BetaTestingStepSettings;
}

export interface BetaTestingStepSettings {
    betaTestingStyle: BetaTestingStyle;
    prescribedBetaSoakDurationSeconds: number;
    googleTestingTrackName: Nullable<string>;
    upstreamCIStepConfigId: Nullable<string>;
    upstreamCIStepConfigOptions: Record<string, StepConfigSummary>;
}

export interface StepConfigSharedSettings {
    branchConfigId: Nullable<string>;
    tagConfigId: Nullable<string>;
    featureAffiliationConfigId: Nullable<string>;
}

export interface CIDistributionStepConfig extends StepConfigBase {
    archetype: ReleaseStepType.CIDistribution;
    settings: CIDistributionStepSettings;
}

export interface CIDistributionStepSettings {
    appIdentifier: Nullable<string>;
}

export interface CIDistributionStepSettingsRequestValues extends StepConfigSharedSettings {
    appIdentifier: Nullable<string>;
}

export interface BetaTestingStepSettingsRequestValues extends StepConfigSharedSettings {
    betaTestingStyle: BetaTestingStyle;
    betaSoakDurationSeconds: number;
    testingTrack: Nullable<string>;
    upstreamCIStepConfigId: Nullable<string>;
}

export interface MetadataStepConfig extends StepConfigBase {
    archetype: ReleaseStepType.Metadata;
    settings: MetadataStepSettings;
}

export interface DefaultMetadataLocale {
    id: string;
    locale: string;
    displayLocale: string;
    whatsNew: Nullable<string>;
    promoText: Nullable<string>;
}

export interface MetadataStepSettings {
    upstreamBetaStepConfigId: string;
    upstreamBetaStepConfigOptions: Record<string, StepConfigSummary>;
    defaultMetadata: DefaultMetadataLocale[];
    requiresManualApproval: Nullable<boolean>;
}

export interface StepMetadataLocaleData {
    whatsNew: Nullable<string>;
    promoText: Nullable<string>;
}

export interface MetadataStepSettingsRequestValues extends StepConfigSharedSettings {
    upstreamBetaStepConfigId: string;
    defaultMetadataByLocale: Record<string, StepMetadataLocaleData>;
    requiresManualApproval: Nullable<boolean>;
}

export interface ScreenshotsStepConfig extends StepConfigBase {
    archetype: ReleaseStepType.Screenshots;
    settings: ScreenshotsStepSettings;
}

export interface ScreenshotsStepSettings {
    requiresManualApproval: Nullable<boolean>;
}

export interface ScreenshotsStepSettingsRequestValues extends StepConfigSharedSettings {
    requiresManualApproval: Nullable<boolean>;
}

export interface RolloutStepConfig extends StepConfigBase {
    archetype: ReleaseStepType.Rollout;
    settings: RolloutStepSettings;
}

export interface RolloutStepSettings {
    sourceStepConfigId: Nullable<string>;
    sourceStepConfigOptions: Record<string, StepConfigSummary>;
}

export interface RolloutStepSettingsRequestValues extends StepConfigSharedSettings {
    sourceStepConfigId: Nullable<string>;
}

export interface SubmissionStepConfig extends StepConfigBase {
    archetype: ReleaseStepType.Submission;
    settings: SubmissionStepSettings;
}

export interface SubmissionStepSettings extends ReleaseStepSettings {}

export interface SubmissionStepSettingsRequestValues extends ReleaseStepSettingsRequestValues {}

export interface StoreReviewStepConfig extends StepConfigBase {
    archetype: ReleaseStepType.StoreReview;
    settings: StoreReviewStepSettings;
}

export interface StoreReviewStepSettingsRequestValues extends ReleaseStepSettingsRequestValues {}

export interface StoreReviewStepSettings extends ReleaseStepSettings {}

export interface ReleaseStepConfig extends StepConfigBase {
    archetype: ReleaseStepType.Takeoff;
    settings: ReleaseStepSettings;
}

export interface ReleaseStepSettingsRequestValues extends StepConfigSharedSettings {
    appStoreSettings: AppStoreDefaultSettings;
    expoEAS?: Nullable<ExpoEASReleaseStepSettings>;
    encryptionSelection: Nullable<InitialEncryptionOptions>;
    isAppAvailableInFrance: Nullable<boolean>;
    upstreamCIStepConfigId: Nullable<string>;
    retainedBuildsSettings?: RetainedBuildsSettings;
}

export interface ExpoEASReleaseStepSettings {
    initialRolloutPercentage: number;
    appIdentifier: Nullable<string>;
}

export enum InitialEncryptionOptions {
    ProprietaryAlgorithms = 'ProprietaryAlgorithms',
    StandardAlgorithms = 'StandardAlgorithms',
    BothAlgorithms = 'BothAlgorithms',
    NoAlgorithms = 'NoAlgorithms',
}

export interface ReleaseStepSettings {
    appStoreSettings: AppStoreDefaultSettings;
    expoEAS?: Nullable<ExpoEASReleaseStepSettings>;
    encryptionSelection: Nullable<InitialEncryptionOptions>;
    isAppAvailableInFrance: Nullable<boolean>;
    upstreamCIStepConfigId: Nullable<string>;
    upstreamCIStepConfigOptions: Record<string, StepConfigSummary>;
    // Backend-served ASC age rating questionnaire catalog (App Store Connect only).
    ageRatingQuestionSet?: Nullable<AgeRatingQuestionSet>;
    // Current App Store Connect age rating answers, read live from ASC (the source of
    // truth) to seed the editor. App Store Connect only; null when unavailable.
    ageRatingDeclaration?: Nullable<AgeRatingDeclaration>;
    // App-level answers used to seed the editor when this step has no saved answers.
    ageRatingAppDefault?: Nullable<AgeRatingDeclaration>;
    retainedBuildsSettings?: RetainedBuildsSettings;
}

export interface RegressionTestingStepConfig extends StepConfigBase {
    archetype: ReleaseStepType.RegressionTesting;
    settings: RegressionTestingStepSettings;
}

export interface RegressionTestingStepSettings {
    regressionTestingStrategy: RegressionTestingStrategy;
    upstreamCIStepConfigId: Nullable<string>;
    upstreamCIStepConfigOptions: Record<string, StepConfigSummary>;
}
